import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { request } from "../store/api/organization/ipsecVendorsGet";
import { IPSecVendorDTO } from "@p81-common/networking-dtos";

export enum VendorType {
  Cloud = 'cloud',
  OnPrem = 'onprem',
  All = 'all',
}

interface UseIPSecVendorsOptions {
  type?: VendorType;
}

interface UseIPSecVendorsReturn {
  ipsecVendors: IPSecVendorDTO[];
  loading: boolean;
  error: any | null;
}

/**
 * Custom hook to manage IPSec vendors data with caching and smart retry logic
 *
 * Behavior:
 * - On success: Caches data, reuses it on subsequent mounts (no refetch)
 * - On error: Retries when component remounts (e.g., returning to wizard step)
 * - Prevents infinite retry loops within the same component mount
 *
 * @param {Object} options - Configuration options
 * @param {VendorType} options.type - Filter by vendor type: VendorType.Cloud, VendorType.OnPrem, or VendorType.All (default: VendorType.All)
 * @returns {Object} { ipsecVendors, loading, error }
 */
export function useIPSecVendors(
  { type = VendorType.All }: UseIPSecVendorsOptions = {}
): UseIPSecVendorsReturn {
  const dispatch = useDispatch();
  const attemptedRef = useRef(false);

  // Access state directly
  const apiVendors = useSelector((state: any) => state.ipsecVendorsGet?.ipsecVendors);
  const loading = useSelector((state: any) => state.ipsecVendorsGet?.loading || false);
  const error = useSelector((state: any) => state.ipsecVendorsGet?.error || null);
  const lastFetch = useSelector((state: any) => state.ipsecVendorsGet?.lastFetch);

  // Filter vendors by type if needed
  const ipsecVendors: IPSecVendorDTO[] = apiVendors && type !== VendorType.All
    ? apiVendors.filter((vendor: IPSecVendorDTO) => vendor.type === type)
    : apiVendors || [];

  useEffect(() => {
    // Fetch if:
    // 1. Never attempted before (!lastFetch), OR
    // 2. Previous attempt failed (error exists) - allows retry on remount
    // AND we haven't attempted yet in this mount (prevents infinite loops)
    const shouldFetch = (!lastFetch || error) && !attemptedRef.current;

    if (shouldFetch && !loading) {
      attemptedRef.current = true;
      dispatch(request());
    }
  }, [dispatch, lastFetch, error, loading]);

  return {
    ipsecVendors,
    loading,
    error,
  };
}
